import clsx from "clsx";

import {
  ITicketSummary,
  TicketPriority,
  TicketQuickFilter,
  TicketStatus,
} from "../../models/Tickets/Ticket";
import { IStaffUser } from "../../models/StaffUser/StaffUser";
import TicketAvgResponseTable from "./TicketAvgResponseTable";
import TicketUserSummaryTable from "./TicketUserSummaryTable";
import {
  priorityAccentMap,
  statusAccentMap,
  ticketPriorityLabels,
  ticketPriorityOptions,
  ticketStatusLabels,
  ticketStatusOptions,
} from "./ticketMeta";

export interface TicketSummarySelection {
  status?: TicketStatus;
  priority?: TicketPriority;
  quickFilter?: TicketQuickFilter;
  assignedToUserId?: string;
}

interface TicketSummaryBarProps {
  summary?: ITicketSummary;
  isLoading: boolean;
  selection: TicketSummarySelection;
  customerName?: string;
  staffUsers?: IStaffUser[];
  onSelect: (selection: TicketSummarySelection) => void;
}

interface SummaryTileProps {
  label: string;
  count: number;
  accentClassName: string;
  active: boolean;
  onClick: () => void;
}

function SummaryTile({
  label,
  count,
  accentClassName,
  active,
  onClick,
}: SummaryTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        "flex min-w-[9rem] flex-1 flex-col items-start rounded-md border border-l-4 bg-white px-3 py-2 text-left transition hover:shadow",
        accentClassName,
        active ? "border-orange-500 ring-2 ring-orange-400" : "border-gray-200",
      )}
    >
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-2xl font-semibold text-slate-800">{count}</span>
    </button>
  );
}

interface SummaryChipProps {
  label: string;
  count: number;
  className: string;
  active: boolean;
  onClick: () => void;
}

function SummaryChip({
  label,
  count,
  className,
  active,
  onClick,
}: SummaryChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        "inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm transition hover:shadow",
        className,
        active ? "ring-2 ring-orange-400" : "",
      )}
    >
      <span className="font-semibold">{count}</span>
      <span>{label}</span>
    </button>
  );
}

export default function TicketSummaryBar({
  summary,
  isLoading,
  selection,
  customerName,
  staffUsers,
  onSelect,
}: TicketSummaryBarProps) {
  if (isLoading && !summary) {
    return <div className="mb-4 h-40 animate-pulse rounded-md bg-gray-100" />;
  }

  if (!summary) return null;

  const statusCounts: Record<TicketStatus, number> = {
    [TicketStatus.Open]: summary.open,
    [TicketStatus.InProgress]: summary.inProgress,
    [TicketStatus.Resolved]: summary.resolved,
    [TicketStatus.Closed]: summary.closed,
  };

  const priorityCounts: Record<TicketPriority, number> = {
    [TicketPriority.Low]: summary.low,
    [TicketPriority.Medium]: summary.medium,
    [TicketPriority.High]: summary.high,
    [TicketPriority.Urgent]: summary.urgent,
  };

  // Clicking the active tile clears it, so a second click gets you back to everything.
  const toggleStatus = (status: TicketStatus) =>
    onSelect({ status: selection.status === status ? undefined : status });

  const togglePriority = (priority: TicketPriority) =>
    onSelect({
      priority: selection.priority === priority ? undefined : priority,
    });

  const toggleQuickFilter = (quickFilter: TicketQuickFilter) =>
    onSelect({
      quickFilter:
        selection.quickFilter === quickFilter ? undefined : quickFilter,
    });

  const chips: {
    label: string;
    count: number;
    quickFilter: TicketQuickFilter;
    className: string;
  }[] = [
    {
      label: `active tickets (${customerName ?? "all customers"})`,
      count: summary.active,
      quickFilter: "Active",
      className: "border-orange-200 bg-orange-50 text-orange-800",
    },
    {
      label: "unassigned",
      count: summary.unassigned,
      quickFilter: "Unassigned",
      className: "border-slate-200 bg-white text-slate-700",
    },
    {
      label: "awaiting first reply",
      count: summary.awaitingFirstReply,
      quickFilter: "AwaitingFirstReply",
      className: "border-rose-200 bg-rose-50 text-rose-800",
    },
    {
      label: "awaiting response",
      count: summary.awaitingResponse,
      quickFilter: "AwaitingResponse",
      className: "border-yellow-200 bg-yellow-50 text-yellow-800",
    },
    {
      label: "created today",
      count: summary.createdToday,
      quickFilter: "CreatedToday",
      className: "border-blue-200 bg-blue-50 text-blue-800",
    },
    {
      label: "created this week",
      count: summary.createdThisWeek,
      quickFilter: "CreatedThisWeek",
      className: "border-blue-200 bg-blue-50 text-blue-800",
    },
    {
      label: "resolved / closed today",
      count: summary.closedToday,
      quickFilter: "ClosedToday",
      className: "border-green-200 bg-green-50 text-green-800",
    },
    {
      label: "resolved / closed this week",
      count: summary.closedThisWeek,
      quickFilter: "ClosedThisWeek",
      className: "border-green-200 bg-green-50 text-green-800",
    },
  ];

  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-start gap-6">
        <TicketUserSummaryTable
          summary={summary}
          staffUsers={staffUsers}
          selectedAssignee={selection.assignedToUserId}
          unassignedSelected={selection.quickFilter === "Unassigned"}
          onSelectAssignee={(assignedToUserId) =>
            onSelect({ assignedToUserId })
          }
          onSelectUnassigned={() => toggleQuickFilter("Unassigned")}
        />
        <TicketAvgResponseTable avgResponse={summary.avgResponse} />
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
          By Status
        </p>
        <div className="flex flex-wrap gap-2">
          {ticketStatusOptions.map((status) => (
            <SummaryTile
              key={status}
              label={ticketStatusLabels[status]}
              count={statusCounts[status]}
              accentClassName={statusAccentMap[status]}
              active={selection.status === status}
              onClick={() => toggleStatus(status)}
            />
          ))}
        </div>
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
          By Priority
        </p>
        <div className="flex flex-wrap gap-2">
          {ticketPriorityOptions.map((priority) => (
            <SummaryTile
              key={priority}
              label={ticketPriorityLabels[priority]}
              count={priorityCounts[priority]}
              accentClassName={priorityAccentMap[priority]}
              active={selection.priority === priority}
              onClick={() => togglePriority(priority)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <SummaryChip
            key={chip.quickFilter}
            label={chip.label}
            count={chip.count}
            className={chip.className}
            active={selection.quickFilter === chip.quickFilter}
            onClick={() => toggleQuickFilter(chip.quickFilter)}
          />
        ))}
      </div>
    </div>
  );
}
